import { useEffect, useRef, useState } from 'react';
import type { AppNotification } from '@neuroedge/shared';
import { listNotifications } from '../../api/notifications';

/**
 * Opt-in desktop pop-ups (+ a short chime) for new notifications while the
 * app is open in some tab. No push service: the bell already polls the
 * unread count, and when it goes up we fetch the newest unread items and
 * show the ones that arrived since the last check.
 *
 * The on/off choice is a per-browser convenience (localStorage); the
 * browser's own permission prompt is the real gate.
 */

const STORAGE_KEY = 'ne.desktopAlerts';

export function desktopAlertsSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

function readSwitch(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'on';
  } catch {
    return false;
  }
}

function writeSwitch(on: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, on ? 'on' : 'off');
  } catch {
    // Private mode / blocked storage - the choice just won't persist.
  }
}

function chime(): void {
  try {
    const Ctx =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(1320, ctx.currentTime + 0.12);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.4);
    osc.onended = () => void ctx.close();
  } catch {
    // Sound is a nicety only.
  }
}

/**
 * `enabled`: the user's switch AND the browser permission. `enable()` asks
 * the browser (must be called from a click). `unreadCount` is the bell's
 * polled count; `onOpen` is called when a pop-up is clicked.
 */
export function useDesktopAlerts(
  unreadCount: number | undefined,
  onOpen: (notification: AppNotification) => void,
) {
  const supported = desktopAlertsSupported();
  const [enabled, setEnabled] = useState(
    () => supported && readSwitch() && Notification.permission === 'granted',
  );
  const lastCount = useRef<number | undefined>(undefined);
  const since = useRef(Date.now());

  useEffect(() => {
    const previous = lastCount.current;
    lastCount.current = unreadCount;
    if (!enabled || unreadCount === undefined || previous === undefined) return;
    if (unreadCount <= previous) return;

    const cutoff = since.current;
    since.current = Date.now();
    void listNotifications({ unreadOnly: true, pageSize: 5 })
      .then(({ items }) => {
        const fresh = items.filter((n) => new Date(n.createdAt).getTime() > cutoff - 60_000);
        if (!fresh.length) return;
        chime();
        for (const n of fresh.slice(0, 3)) {
          const popup = new Notification(n.title, {
            body: n.body ?? undefined,
            tag: n.id,
          });
          popup.onclick = () => {
            window.focus();
            onOpen(n);
            popup.close();
          };
        }
      })
      .catch(() => undefined);
  }, [unreadCount, enabled, onOpen]);

  async function enable(): Promise<boolean> {
    if (!supported) return false;
    const permission =
      Notification.permission === 'default'
        ? await Notification.requestPermission()
        : Notification.permission;
    const on = permission === 'granted';
    writeSwitch(on);
    setEnabled(on);
    return on;
  }

  function disable(): void {
    writeSwitch(false);
    setEnabled(false);
  }

  return {
    supported,
    enabled,
    blocked: supported && Notification.permission === 'denied',
    enable,
    disable,
  };
}
